import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Package2, Pencil, Trash2, Upload, Download, Loader2, MoreVertical, Layers, Hash, Building2, Tag, FileText, Boxes, Edit } from "lucide-react";
import client, { apiErrorMessage, API } from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import ListToolbar from '@/components/ListToolbar';
import Pagination from '@/components/PaginationBar';
import EmptyState from '@/components/EmptyState';
import { TableSkeleton } from '@/components/Skeletons';
import { Button } from '@/components/ui/button';
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { formatDate } from '@/lib/format';
import { auth as authStore } from '@/lib/api';

const EMPTY = { name: '', companyName: '', sku: '', category: '', unit: '', description: '' };

export default function ProductMasterPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const q = useQuery({
    queryKey: ['product-master', { page, search }],
    queryFn: () => client.get('/products', { params: { page, limit: 20, search: search || undefined } }).then((r) => r.data),
  });
  const items = q.data?.data || [];
  const meta = q.data?.meta?.pagination || { page: 1, totalPages: 1 };

  async function del(p) {
    try {
      await client.delete(`/products/${p.id}`);
      toast.success('Product deleted');
      qc.invalidateQueries({ queryKey: ['product-master'] });
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }

  async function downloadTemplate() {
    try {
      const token = authStore.getAccessToken();
      const res = await fetch(`${API}/products/template`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'product-master-template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiErrorMessage(err)); }
  }

  return (
    <div data-testid="product-master-page">
      <PageHeader
        title="Product master"
        description="Central product list used across all customers and stock additions."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadTemplate} className="h-10" data-testid="pm-template-btn">
              <Download className="w-4 h-4 mr-1.5" /> Template
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} className="h-10" data-testid="pm-import-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Import Excel
            </Button>
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-zinc-950 hover:bg-zinc-800 text-white h-10" data-testid="pm-create-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add product
            </Button>
          </div>
        }
      />

      <div className="mb-4">
        <ListToolbar search={search} onSearchChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search by name, company, SKU…" testIdPrefix="pm" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        {q.isLoading ? <TableSkeleton cols={5} /> : items.length === 0 ? (
          <EmptyState icon={Package2} title="No products yet" description="Add products individually or import from Excel." />
        ) : (
          <table className="w-full text-sm" data-testid="pm-table">
            <thead>
              <tr className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wider">
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">SKU</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Unit</th>
                <th className="text-left px-4 py-3">Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-zinc-50" data-testid={`pm-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-zinc-700">{p.companyName || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.sku || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-4 py-3 text-zinc-700">{p.category || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-4 py-3 text-zinc-700">{p.unit || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{formatDate(p.createdAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`pm-actions-${p.id}`}><MoreVertical className="w-4 h-4 text-zinc-500" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem onClick={() => { setEditing(p); setFormOpen(true); }} data-testid={`pm-edit-${p.id}`}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem className="text-red-600 focus:text-red-700 focus:bg-red-50" onSelect={(e) => e.preventDefault()} data-testid={`pm-del-${p.id}`}>
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{p.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>Soft-deletes this product from the master list. Existing inventory records are not affected.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => del(p)} data-testid={`pm-del-confirm-${p.id}`}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination page={meta.page} totalPages={meta.totalPages} onChange={setPage} />
      </div>

      <ProductFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        onSaved={() => { setFormOpen(false); qc.invalidateQueries({ queryKey: ['product-master'] }); }}
      />

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={() => { setImportOpen(false); qc.invalidateQueries({ queryKey: ['product-master'] }); }} />
    </div>
  );
}

function ProductFormDialog({ open, onOpenChange, initial, onSaved }) {
  const catQ = useQuery({
    queryKey: ["category-masters"],
    queryFn: () => client.get("/category-masters").then((r) => r.data.data || []),
    enabled: open,
    staleTime: 60_000,
  });
  const compQ = useQuery({
    queryKey: ["company-masters"],
    queryFn: () => client.get("/company-masters").then((r) => r.data.data || []),
    enabled: open,
    staleTime: 60_000,
  });
  const categories = catQ.data || [];
  const companies = compQ.data || [];
  const isEdit = Boolean(initial);
  const [form, setForm] = useState({
    name: "",
    companyName: "",
    sku: "",
    category: "",
    unit: "CARTONS",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        name: initial.name || "",
        companyName: initial.companyName || "",
        sku: initial.sku || "",
        category: initial.category || "",
        unit: initial.unit || "CARTONS",
        description: initial.description || "",
      });
    } else {
      setForm({
        name: "",
        companyName: "",
        sku: "",
        category: "",
        unit: "CARTONS",
        description: "",
      });
    }
  }, [initial, open]);

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Product name is required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        companyName: form.companyName?.trim() || null,
        sku: form.sku?.trim() || null,
        category: form.category?.trim() || null,
        unit: form.unit?.trim() || null,
        description: form.description?.trim() || null,
      };
      const res = isEdit
        ? await client.patch(`/products/${initial.id}`, payload)
        : await client.post("/products", payload);
      toast.success(isEdit ? "Product updated" : "Product added");
      onSaved(res.data.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  const commonUnits = ["CARTONS", "BOX", "PCS", "KG", "LTR", "BAG", "PALLET"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white p-0 rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden" data-testid="pm-form-dialog">
        <div className="px-6 pt-5 pb-4 border-b border-zinc-100 bg-zinc-50/70">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-zinc-950 text-white shadow-sm">
              <Boxes className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold tracking-tight text-zinc-950">
                {isEdit ? "Edit Product" : "Add Product"}
              </DialogTitle>
              <p className="text-xs text-zinc-500 mt-0.5">
                {isEdit ? "Update master catalog product specifications" : "Register a new product into your warehouse catalog"}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5 mb-1.5">
              <Tag className="w-3.5 h-3.5 text-zinc-400" />
              Product Name <span className="text-rose-500">*</span>
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Paracetamol 500mg, Cotton T-Shirt XL"
              className="h-10 text-xs rounded-lg border-zinc-200 focus-visible:ring-zinc-950"
              required
              data-testid="pm-form-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <Label className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5 mb-1.5">
                <Building2 className="w-3.5 h-3.5 text-zinc-400" />
                Company Name
              </Label>
              <Select
                value={form.companyName || "none"}
                onValueChange={(val) => setForm({ ...form, companyName: val === "none" ? "" : val })}
              >
                <SelectTrigger className="h-10 text-xs rounded-lg border-zinc-200" data-testid="pm-form-company">
                  <SelectValue placeholder={compQ.isLoading ? "Loading companies..." : "Select company..."} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="none" className="text-zinc-400">None / Clear</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5 mb-1.5">
                <Hash className="w-3.5 h-3.5 text-zinc-400" />
                SKU / Item Code
              </Label>
              <Input
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                placeholder="e.g. MED-55-2026"
                className="h-10 text-xs font-mono uppercase tracking-wider rounded-lg border-zinc-200 focus-visible:ring-zinc-950"
                data-testid="pm-form-sku"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <Label className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5 mb-1.5">
                <Layers className="w-3.5 h-3.5 text-zinc-400" />
                Category
              </Label>
              <Select
                value={form.category || "none"}
                onValueChange={(val) => setForm({ ...form, category: val === "none" ? "" : val })}
              >
                <SelectTrigger className="h-10 text-xs rounded-lg border-zinc-200" data-testid="pm-form-category">
                  <SelectValue placeholder={catQ.isLoading ? "Loading categories..." : "Select category..."} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="none" className="text-zinc-400">None / Clear</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5 mb-1.5">
                <Boxes className="w-3.5 h-3.5 text-zinc-400" />
                Unit
              </Label>
              <div className="flex gap-2">
                <Input
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value.toUpperCase() })}
                  placeholder="e.g. CARTONS"
                  className="h-10 text-xs font-mono uppercase rounded-lg border-zinc-200 flex-1 focus-visible:ring-zinc-950"
                  data-testid="pm-form-unit"
                />
                <select
                  className="h-10 px-2 text-xs bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded-lg cursor-pointer text-zinc-700 transition"
                  onChange={(e) => {
                    if (e.target.value) setForm({ ...form, unit: e.target.value });
                  }}
                  value=""
                >
                  <option value="" disabled>Presets</option>
                  {commonUnits.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5 mb-1.5">
              <FileText className="w-3.5 h-3.5 text-zinc-400" />
              Description
            </Label>
            <Textarea
              value={form.description}
              rows={2}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional notes, handling specifications..."
              className="text-xs rounded-lg border-zinc-200 resize-none focus-visible:ring-zinc-950"
              data-testid="pm-form-desc"
            />
          </div>

          <div className="pt-3 border-t border-zinc-100 flex items-center justify-end gap-2.5">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-9 px-4 text-xs font-medium rounded-lg border-zinc-200 hover:bg-zinc-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !form.name.trim()}
              className="h-9 px-5 text-xs font-medium bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg shadow-sm flex items-center gap-1.5 transition"
              data-testid="pm-form-submit"
            >
              {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {isEdit ? "Save changes" : "Add product"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, onOpenChange, onDone }) {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => { if (open) { setResult(null); if (fileRef.current) fileRef.current.value = ''; } }, [open]);

  async function submit(e) {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error('Choose a .xlsx file'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const token = authStore.getAccessToken();
      const res = await fetch(`${API}/products/import`, {
        method: 'POST', body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json?.error?.message || json?.message || 'Import failed');
      setResult(json.data);
      toast.success(`Imported ${json.data.created} of ${json.data.total} (${json.data.skipped} skipped, ${json.data.failed} failed)`);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pm-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Import products from Excel</DialogTitle>
          <DialogDescription className="text-xs">Upload an .xlsx file with columns: name, companyName, sku, category, unit, description. Duplicates are skipped by name + company.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Excel file (.xlsx)*</Label>
            <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" required className="mt-1 block w-full text-sm text-zinc-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-zinc-100 file:hover:bg-zinc-200 file:cursor-pointer" data-testid="pm-import-file" />
          </div>
          {result && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm space-y-1" data-testid="pm-import-result">
              <div className="font-mono">Total: <strong>{result.total}</strong> · Created: <strong className="text-emerald-700">{result.created}</strong> · Skipped: <strong className="text-yellow-700">{result.skipped}</strong> · Failed: <strong className="text-red-700">{result.failed}</strong></div>
              {result.errors?.length > 0 && (
                <div className="mt-2 text-xs text-red-700 max-h-32 overflow-y-auto">
                  {result.errors.slice(0, 10).map((e, i) => <div key={i}>Row {e.row}: {e.message}</div>)}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{result ? 'Close' : 'Cancel'}</Button>
            {!result && (
              <Button type="submit" disabled={loading} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="pm-import-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Import</>}
              </Button>
            )}
            {result && (
              <Button type="button" onClick={onDone} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="pm-import-done">Done</Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
